import { useCallback, useEffect, useRef, useState } from "react";
import notificationsService from "services/notifications-service";

export function notificationSnapshot(value) {
  const rows = Array.isArray(value) ? value : value?.notifications;
  if (!Array.isArray(rows)) return null;
  return {
    notifications: rows.slice(0, 50),
    unreadCount: Number.isSafeInteger(value?.unreadCount) && value.unreadCount >= 0
      ? value.unreadCount : rows.length,
    nextCursor: Number.isSafeInteger(value?.nextCursor) && value.nextCursor > 0
      ? value.nextCursor : null,
  };
}

export default function useNotificationFeed() {
  const [feed, setFeed] = useState({ notifications: [], unreadCount: 0, nextCursor: null, headKey: "" });
  const [loadingMore, setLoadingMore] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState(null);
  const headKey = useRef("");
  const pageInFlight = useRef(false);
  const readInFlight = useRef(false);
  const active = useRef(true);
  const latestSnapshotStartedAt = useRef(0);
  const readBarrier = useRef(0);

  const applySnapshot = useCallback((value) => {
    const startedAt = value?.requestStartedAt;
    if (Number.isFinite(startedAt)) {
      if (startedAt < readBarrier.current || startedAt < latestSnapshotStartedAt.current) return;
      latestSnapshotStartedAt.current = startedAt;
    }
    const snapshot = notificationSnapshot(value);
    if (!snapshot || !active.current) return;
    const key = JSON.stringify(snapshot);
    headKey.current = key;
    // Unrelated responses must not collapse pages the user already loaded.
    // A changed head/count resets pagination so read/deleted rows cannot linger.
    setFeed((current) => current.headKey === key ? current : { ...snapshot, headKey: key });
  }, []);

  useEffect(() => {
    active.current = true;
    const onUpdate = (event) => applySnapshot(event.detail);
    document.addEventListener("notifications-updated", onUpdate);
    return () => {
      active.current = false;
      document.removeEventListener("notifications-updated", onUpdate);
    };
  }, [applySnapshot]);

  async function loadMore() {
    if (feed.nextCursor == null || pageInFlight.current || readInFlight.current) return;
    const key = headKey.current;
    pageInFlight.current = true;
    setLoadingMore(true);
    setError(null);
    try {
      const [response, page] = await notificationsService.getNotifications({ beforeId: feed.nextCursor });
      if (!active.current || key !== headKey.current) return;
      if (!response.ok || !Array.isArray(page?.data)) throw new Error("Could not load older notifications.");
      setFeed((current) => {
        if (current.headKey !== key) return current;
        const ids = new Set(current.notifications.map((row) => row.id));
        const older = page.data.slice(0, 50).filter((row) => {
          if (ids.has(row.id)) return false;
          ids.add(row.id);
          return true;
        });
        return { ...current, notifications: [...current.notifications, ...older], nextCursor: page.nextCursor };
      });
    } catch {
      if (active.current && key === headKey.current) setError("Could not load older notifications. Please try again.");
    } finally {
      pageInFlight.current = false;
      if (active.current) setLoadingMore(false);
    }
  }

  async function markRead(id) {
    if (readInFlight.current) return;
    readInFlight.current = true;
    setReading(true);
    setError(null);
    try {
      const [response] = id == null
        ? await notificationsService.markAllNotificationsAsRead()
        : await notificationsService.markNotificationAsRead(id);
      if (!response.ok) throw new Error("Could not mark notifications as read.");
      // Ignore snapshots from requests already in flight when this write finished.
      readBarrier.current = performance.now();
      // HttpService also emits this snapshot. Applying it twice is harmless and
      // never decrements the badge twice. Failed writes keep the current state.
      if (Array.isArray(response.data?.notifications)) applySnapshot({
        notifications: response.data.notifications,
        unreadCount: response.data.unreadNotificationCount,
        nextCursor: response.data.notificationsNextCursor,
      });
    } catch {
      if (active.current) setError("Could not mark notifications as read. Please try again.");
    } finally {
      readInFlight.current = false;
      if (active.current) setReading(false);
    }
  }

  return { ...feed, loadingMore, reading, error, loadMore, markRead };
}
